import type { FC } from 'react';
import dayjs from 'dayjs';
import type { Frequency, WeekStartDay } from '../../../types/calendar';

const WEEK_START_DAY_NUM: Record<WeekStartDay, number> = {
    Sunday: 0,
    Monday: 1,
    Saturday: 6,
};

/**
 * Day offsets (from `periodStartDate`) where a real calendar week begins, per
 * `weekStartDay`. Anchoring to the actual weekday (rather than counting flat
 * 7-day steps from the period start) keeps dividers aligned to the same weeks
 * across a month boundary — e.g. a week that starts in Jan and ends in Feb
 * still lands 7 days apart in both columns. Day 0 (the cell's own left edge)
 * is never included even when the period happens to start on the configured
 * week-start day.
 */
function getWeekStartOffsets(periodStartDate: string, totalDays: number, weekStartDay: WeekStartDay): number[] {
    const startDow = dayjs(periodStartDate).day();
    const targetDow = WEEK_START_DAY_NUM[weekStartDay];
    const firstOffset = ((targetDow - startDow + 7) % 7) || 7;

    const offsets: number[] = [];
    for (let day = firstOffset; day < totalDays; day += 7) {
        offsets.push(day);
    }
    return offsets;
}

export interface CalendarIntervalProps {
    frequency: Frequency;
    periodStartDate: string;
    cellWidth: number;
    pxPerDay: number;
    /**
     * Actual day span of this period (period_end_date - period_start_date + 1), i.e. the same
     * value used to derive `pxPerDay`. Divider positions are filtered against it (strictly less
     * than) rather than against a recomputed calendar-month/quarter length, so a boundary-exact
     * or partial period never places a line on or past the cell's own right edge.
     */
    totalDays: number;
    /** Tenant-configured first day of the week (e.g. Monday for Danone); aligns monthly dividers to real weeks. */
    weekStartDay: WeekStartDay;
}

const CalendarInterval: FC<CalendarIntervalProps> = ({
    frequency,
    periodStartDate,
    cellWidth,
    pxPerDay,
    totalDays,
    weekStartDay,
}) => {
    if (frequency === 'W') {
        const sectionWidth = cellWidth / 7;
        return (
            <div className="pointer-events-none absolute inset-0 z-0">
                {Array.from({ length: 6 }, (_, i) => (
                    <div
                        key={i}
                        className="absolute top-0 bottom-0 border-r border-dotted border-[#D4D4D8]"
                        style={{ left: (i + 1) * sectionWidth }}
                    />
                ))}
            </div>
        );
    }

    if (frequency === 'Q') {
        const qStart = dayjs(periodStartDate);
        const monthBoundaryDays = [qStart.add(1, 'month').diff(qStart, 'day'), qStart.add(2, 'month').diff(qStart, 'day')].filter(
            (day) => day < totalDays,
        );
        return (
            <div className="pointer-events-none absolute inset-0 z-0">
                {monthBoundaryDays.map((day) => (
                    <div
                        key={day}
                        className="absolute top-0 bottom-0 border-r border-dashed border-[#D4D4D8]"
                        style={{ left: day * pxPerDay }}
                    />
                ))}
            </div>
        );
    }

    const weekBoundaryDays = getWeekStartOffsets(periodStartDate, totalDays, weekStartDay);
    return (
        <div className="pointer-events-none absolute inset-0 z-0">
            {weekBoundaryDays.map((day) => (
                <div
                    key={day}
                    className="absolute top-0 bottom-0 border-r border-dashed border-[#D4D4D8]"
                    style={{ left: day * pxPerDay }}
                />
            ))}
        </div>
    );
};

CalendarInterval.displayName = 'CalendarInterval';
export default CalendarInterval;
